import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Reveal, MaskLine, SectionLabel } from "@/components/Reveal";

const steps = [
  { n: "01", title: "Understand", text: "Understand your business, role requirements, skills, and hiring objectives." },
  { n: "02", title: "Identify", text: "Source and identify relevant professionals through focused talent search." },
  { n: "03", title: "Evaluate", text: "Screen and assess candidates against the requirements of the role." },
  { n: "04", title: "Connect", text: "Present suitable candidates and support the hiring process through closure." },
];

const services = [
  { n: "01", title: "Permanent Recruitment", text: "Find professionals who can grow with your organization and contribute for the long term.", to: "/services/permanent-recruitment", testid: "service-card-permanent" },
  { n: "02", title: "Contract Staffing", text: "Access skilled professionals for project-based, temporary, or evolving workforce requirements.", to: "/services/contract-staffing", testid: "service-card-contract" },
  { n: "03", title: "RPO", text: "Extend your recruitment capability with a flexible, dedicated approach to managing hiring needs.", to: "/services/rpo", testid: "service-card-rpo" },
  { n: "04", title: "Payroll Outsourcing", text: "Simplify payroll administration and workforce processes so your team can focus on what matters.", to: "/services/payroll-management", testid: "service-card-payroll" },
];

const industries = [
  { n: "01", title: "IT / SaaS", text: "Engineering, product, data, and digital roles." },
  { n: "02", title: "BPO / ITES", text: "Customer experience, operations, and support teams." },
  { n: "03", title: "BFSI", text: "Banking, financial services, and insurance professionals." },
  { n: "04", title: "Healthcare", text: "Clinical support, operations, and healthcare services roles." },
  { n: "05", title: "E-commerce", text: "Category, marketplace, growth, and supply chain roles." },
  { n: "06", title: "Manufacturing", text: "Plant operations, engineering, quality, and supply chain." },
];

const principles = [
  { title: "Relevant Talent", text: "We focus on suitability, experience, expectations, and the requirements behind every role — not simply matching keywords on a CV." },
  { title: "Understanding Requirements", text: "Every search begins with understanding your business, the role, and what the right hire looks like for your team." },
  { title: "Clear Communication", text: "We keep clients and candidates informed, aligned, and comfortable with the next step at every stage." },
  { title: "Long-Term Relationships", text: "We believe strong relationships should extend beyond a single placement or hiring requirement." },
];

const employerFaqs = [
  { q: "What hiring and workforce services does Genspire provide?", a: "Genspire provides Permanent Recruitment, Contract Staffing, Recruitment Process Outsourcing (RPO), and Payroll Outsourcing services." },
  { q: "How do you understand our hiring requirements?", a: "We begin by understanding the role, required experience and skills, business context, location, timeline, and other factors that define the requirement." },
  { q: "How are candidates screened?", a: "Candidates are evaluated against relevant factors such as experience, skills, expectations, availability, and the requirements of the role." },
  { q: "Do you support permanent and contract hiring?", a: "Yes. Genspire supports both permanent recruitment and contract staffing requirements." },
  { q: "How quickly can we expect relevant candidates?", a: "The timeline depends on factors such as the role, skills required, location, market availability, and hiring requirements. We do not promise a fixed turnaround unless specifically agreed." },
  { q: "What happens after we submit a requirement?", a: "Your requirement is received by Genspire for review. Our team can then connect with you to understand the requirement further and discuss the next steps." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-[112px] pb-14 lg:pt-[136px] lg:pb-16" data-testid="hero-section">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full grid grid-cols-2 lg:grid-cols-4">
          <div className="border-l border-slate-200/60" />
          <div className="border-l border-slate-200/60" />
          <div className="border-l border-slate-200/60 hidden lg:block" />
          <div className="border-l border-r border-slate-200/60 hidden lg:block" />
        </div>
      </div>
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] gs-spin-slow">
        <svg viewBox="0 0 100 100" className="h-full w-full">
          <circle cx="50" cy="50" r="48" fill="none" stroke="#B53331" strokeOpacity="0.32" strokeWidth="0.4" strokeDasharray="1.5 4" />
        </svg>
      </div>
      <span aria-hidden className="pointer-events-none absolute left-[6%] bottom-[18%] h-2.5 w-2.5 rotate-45 bg-brand-red/70 hidden lg:block" />

      <motion.div style={{ y: textY }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-6xl lg:text-[4.6rem] leading-[1.05] max-w-5xl">
          <MaskLine delay={0.2}>The right <span className="text-brand-red">talent</span> for the</MaskLine>
          <MaskLine delay={0.32}>roles that matter.</MaskLine>
        </h1>
        <MaskLine delay={0.46} className="mt-8 max-w-2xl">
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="hero-supporting-text">
            Genspire helps organizations find, hire, and build teams with the right professional talent across key industries and functions.
          </p>
        </MaskLine>
        <Reveal delay={0.6} className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Link to="/discuss-your-requirement" data-testid="hero-cta-hire"
              className="group inline-flex items-center justify-center gap-3 bg-brand-red px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-brand-dark">
              Hire Talent Now
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/how-genspire-works" data-testid="hero-cta-how-we-work"
              className="group inline-flex items-center justify-center gap-3 border border-slate-300 px-8 py-4 text-sm font-medium tracking-wide text-slate-900 transition-colors duration-300 hover:border-brand-red hover:text-brand-red">
              See How We Work
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </motion.div>
    </section>
  );
}

function Services() {
  return (
    <section className="py-14 lg:py-20 bg-brand-light/60" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 lg:gap-8">
            <SectionLabel>Genspire Services</SectionLabel>
            <p className="text-slate-500 max-w-md text-sm sm:text-base lg:mb-5">
              From finding the right individual to supporting broader workforce requirements, Genspire provides practical staffing and recruitment solutions designed around your organization.
            </p>
          </div>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-2 border-t border-l border-slate-200">
          {services.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.07}>
              <Link to={s.to} data-testid={s.testid}
                className="group relative block bg-white border-r border-b border-slate-200 p-8 sm:p-10 h-full transition-colors duration-500 hover:bg-brand-light/30 overflow-hidden">
                <div className="absolute top-0 left-0 h-[3px] w-0 bg-brand-red transition-all duration-500 group-hover:w-full" />
                <span className="font-mono text-xs text-brand-red font-medium">{s.n}</span>
                <h3 className="mt-5 font-display font-bold text-slate-900 text-lg sm:text-xl uppercase tracking-wide">{s.title}</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed max-w-md">{s.text}</p>
                <span className="mt-7 inline-flex items-center gap-3 text-sm font-medium text-slate-900 group-hover:text-brand-red transition-colors">
                  <span className="h-px w-7 bg-current transition-all duration-500 group-hover:w-11" />
                  Learn More
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <Link to="/services" data-testid="services-explore-cta"
            className="group mt-10 inline-flex items-center gap-3 text-sm font-medium text-brand-red">
            <span className="h-px w-8 bg-brand-red transition-all duration-500 group-hover:w-14" />
            Explore Our Services
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section className="py-14 lg:py-20" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionLabel>Industries We Serve</SectionLabel>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-4xl lg:text-5xl max-w-2xl leading-[1.12]">
              Focused talent across <span className="text-brand-red">key industries.</span>
            </h2>
            <p className="text-slate-500 max-w-md text-sm sm:text-base">
              Genspire works across sectors where the right hire makes a real difference — understanding the roles, skills, and context behind each requirement.
            </p>
          </div>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-slate-200">
          {industries.map((ind, i) => (
            <Reveal key={ind.n} delay={i * 0.06}>
              <div className="group relative h-full bg-white border-r border-b border-slate-200 p-8 transition-colors duration-500 hover:bg-brand-light/30 overflow-hidden" data-testid={`industry-${i + 1}`}>
                <div className="absolute top-0 left-0 h-[3px] w-0 bg-brand-red transition-all duration-500 group-hover:w-full" />
                <span className="font-mono text-xs text-brand-red font-medium">{ind.n}</span>
                <h3 className="mt-5 font-display font-bold text-slate-900 text-lg uppercase tracking-wide">{ind.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{ind.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="py-14 lg:py-20 bg-brand-ink text-white relative overflow-hidden" data-testid="process-section">
      <div className="pointer-events-none absolute top-0 right-0 font-display font-extrabold text-[26vw] leading-none gs-outline-text opacity-[0.06] select-none" style={{ WebkitTextStroke: "1px rgba(255,255,255,0.4)" }}>
        04
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal>
          <SectionLabel light>How Genspire Works</SectionLabel>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-4xl lg:text-5xl">From Requirement to <span className="text-brand-red">Right Fit</span></h2>
            <p className="text-white/50 max-w-md text-sm sm:text-base">A clear, structured, and human approach to every hiring requirement.</p>
          </div>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="relative" data-testid={`process-step-${s.n}`}>
                <div className="flex items-center gap-4">
                  <span className="font-display font-extrabold text-5xl text-brand-red">{s.n}</span>
                  {i < steps.length - 1 && <span className="hidden lg:block h-px flex-1 bg-gradient-to-r from-brand-red/60 to-white/10" />}
                </div>
                <h3 className="mt-6 font-display font-semibold text-xl">{s.title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyGenspire() {
  return (
    <section className="py-14 lg:py-20" data-testid="why-genspire-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionLabel>Why Genspire</SectionLabel>
          <h2 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-4xl lg:text-5xl max-w-3xl leading-[1.12]">
            More than matching profiles. <span className="text-brand-red">Building relevant connections.</span>
          </h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-slate-200">
          {principles.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="group h-full border-r border-b border-slate-200 p-8 transition-colors duration-500 hover:bg-brand-light/30" data-testid={`principle-${i + 1}`}>
                <span className="font-mono text-xs text-brand-red font-medium">0{i + 1}</span>
                <h3 className="mt-5 font-display font-semibold text-slate-900 text-lg uppercase tracking-wide">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faqs() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-14 lg:py-20 bg-brand-light/60" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionLabel>FAQs</SectionLabel>
          <h2 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-4xl mb-12">
            Questions, <span className="text-brand-red">answered clearly.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="max-w-3xl" data-testid="faq-group-employer">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-red font-medium mb-5">For Employers</p>
            <div className="border-t border-slate-200">
              {employerFaqs.map((f, i) => (
                <div key={i} className="border-b border-slate-200" data-testid={`faq-employer-${i + 1}`}>
                  <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-employer-toggle-${i + 1}`}
                    className="w-full flex items-center justify-between gap-6 py-5 text-left group">
                    <span className={`text-sm sm:text-[15px] font-medium transition-colors ${open === i ? "text-brand-red" : "text-slate-800 group-hover:text-brand-red"}`}>{f.q}</span>
                    <ChevronDown size={15} className={`shrink-0 transition-transform duration-300 ${open === i ? "rotate-180 text-brand-red" : "text-slate-400"}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${open === i ? "max-h-48 pb-5" : "max-h-0"}`}>
                    <p className="text-sm text-slate-600 leading-relaxed pr-6">{f.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="py-16 lg:py-24 bg-brand-red text-white relative overflow-hidden" data-testid="final-cta-section">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full grid grid-cols-2 lg:grid-cols-4">
          <div className="border-l border-white/10" />
          <div className="border-l border-white/10" />
          <div className="border-l border-white/10 hidden lg:block" />
          <div className="border-l border-r border-white/10 hidden lg:block" />
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-white/75 mb-6">Partner in Progress</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-5xl leading-[1.1]">
            Ready to find the <span className="underline decoration-white/40 underline-offset-[10px]">right talent?</span>
          </h2>
          <p className="mt-6 text-white/80 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            Tell us about the role — our team will review your requirement and connect with you on the next steps.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <Link to="/discuss-your-requirement" data-testid="final-cta-hire"
            className="group mt-10 inline-flex items-center gap-3 bg-white px-9 py-4 text-sm font-medium tracking-wide text-brand-red transition-colors duration-300 hover:bg-brand-ink hover:text-white">
            Hire Talent Now
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Services />
      <Industries />
      <Process />
      <WhyGenspire />
      <Faqs />
      <FinalCta />
    </>
  );
}
